import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { PiAirplaneTilt, PiArrowSquareOut, PiClockCountdown, PiPackage, PiWarehouse } from 'react-icons/pi';
import V2Modal from '@v2/components/ui/V2Modal';
import { DOT_STYLE, dotInlineStyle, isNoCustomization } from '@v2/features/colors/ColorDot';
import { fadeZoomIn, useStaggerIn } from '@v2/lib/motion';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import type { OrderProductCard } from '../types';
import { formatBrl } from '../utils';

const P = 'v2.ploomes_deals.product_modal';

type Mode = NonNullable<OrderProductCard['fulfillment_mode']>;

const MODE_STYLE: Record<Mode, string> = {
    stock: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/30 dark:text-emerald-200',
    import: 'border-sky-200 bg-sky-50 text-sky-800 dark:border-sky-800/60 dark:bg-sky-950/30 dark:text-sky-200',
    split: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200',
    pending_stock: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-200',
};

const MODE_ICON = { stock: PiWarehouse, import: PiAirplaneTilt, split: PiPackage, pending_stock: PiClockCountdown } as const;

/**
 * Detalhe do aparelho na venda — mesmo padrão do modal da PO (foto grande + cores do pedido),
 * mais o atendimento da linha: sai do estoque, vai ser importado ou parte de cada.
 */
export default function DealProductDetailModal({ product, onClose }: { product: OrderProductCard | null; onClose: () => void }) {
    const { t } = useI18n();
    const listRef = useRef<HTMLUListElement>(null);
    useStaggerIn(listRef, product ? `${product.id}` : '');

    if (!product) return null;

    const mode: Mode = product.fulfillment_mode ?? 'import';
    const ModeIcon = MODE_ICON[mode];
    const quantity = Math.round(product.quantity);
    const fromStock = product.qty_from_stock ?? (mode === 'stock' ? quantity : 0);
    const toImport = product.qty_to_import ?? (mode === 'import' ? quantity : Math.max(0, quantity - fromStock));
    const request = product.stock_request;
    const rejected = (product.color_check ?? []).filter((c) => !c.accepted);
    const swatches = product.color_swatches;

    return (
        <V2Modal
            open
            onClose={onClose}
            title={product.product_name}
            description={[product.product_code, product.supplier_label].filter(Boolean).join(' · ') || undefined}
            size="5xl"
        >
            <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_24rem]">
                <div className="flex items-center justify-center rounded-xl bg-gray-50 p-3 dark:bg-gray-800/60">
                    {product.image_url ? (
                        <img
                            src={product.image_url}
                            alt={product.product_name}
                            onLoad={(e) => fadeZoomIn(e.currentTarget)}
                            className="max-h-[56vh] w-auto rounded-lg object-contain"
                        />
                    ) : (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                            <PiPackage className="size-14" />
                            <span className="text-xs">{t(`${P}.no_image`)}</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-3">
                    {/* Atendimento */}
                    <section>
                        <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t(`${P}.fulfillment_title`)}
                        </h3>
                        <div className={cn('rounded-xl border px-3 py-2', MODE_STYLE[mode])}>
                            <div className="flex items-center justify-between gap-3">
                                <p className="flex min-w-0 items-center gap-2 text-sm font-bold">
                                    <ModeIcon className="size-5 shrink-0" />
                                    <span className="truncate">{t(`${P}.mode_${mode}`)}</span>
                                </p>
                                <div className="flex shrink-0 gap-3 text-center">
                                    <div>
                                        <p className="text-base font-bold leading-tight tabular-nums">{fromStock}</p>
                                        <p className="text-[9px] font-medium uppercase tracking-wide opacity-80">{t(`${P}.from_stock`)}</p>
                                    </div>
                                    <div>
                                        <p className="text-base font-bold leading-tight tabular-nums">{toImport}</p>
                                        <p className="text-[9px] font-medium uppercase tracking-wide opacity-80">{t(`${P}.to_import`)}</p>
                                    </div>
                                </div>
                            </div>
                            {request && (
                                <p className="mt-1.5 text-xs">
                                    {t(`${P}.reservation`, {
                                        status: t(`${P}.request_${request.status}`),
                                        qty: request.qty_approved ?? request.qty_requested,
                                        source: request.source === 'shipment'
                                            ? (request.source_shipment?.label ?? request.source_label ?? t(`${P}.source_shipment`))
                                            : t(`${P}.source_cd`),
                                    })}
                                    {request.source === 'shipment' && (request.source_shipment?.id ?? request.source_shipment_id) ? (
                                        <Link
                                            to={`/shipments/${request.source_shipment?.id ?? request.source_shipment_id}`}
                                            className="ml-1 inline-flex items-center gap-0.5 font-semibold underline-offset-2 hover:underline"
                                        >
                                            {t(`${P}.open_shipment`)}
                                            <PiArrowSquareOut className="size-3" />
                                        </Link>
                                    ) : null}
                                </p>
                            )}
                        </div>
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                            {t(`${P}.availability`, {
                                cd: product.available_to_request ?? product.stock_available ?? 0,
                                transit: product.shipment_available_total ?? 0,
                            })}
                        </p>
                    </section>

                    {/* Cores do pedido — mesmos estados da PO/PI */}
                    <section>
                        <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            {t(`${P}.colors_title`)}
                        </h3>
                        {swatches.length === 0 ? (
                            <p className="text-sm text-gray-400">{t(`${P}.no_colors`)}</p>
                        ) : (
                            <ul ref={listRef} className="grid grid-cols-2 gap-2">
                                {swatches.map((swatch) => {
                                    const none = !swatch.name || isNoCustomization(swatch.name);
                                    const state = none ? 'none' : swatch.hex ? 'standard' : 'off';
                                    const notAccepted = rejected.find((c) => c.label === swatch.label || c.part === swatch.slot);

                                    return (
                                        <li
                                            key={swatch.slot}
                                            data-motion-item
                                            className="flex min-w-0 items-center gap-2 rounded-xl border border-gray-100 bg-white p-2 shadow-sm dark:border-gray-700/60 dark:bg-gray-800"
                                            title={notAccepted ? t(`${P}.not_accepted`, { colors: notAccepted.accepted_colors.join(', ') }) : undefined}
                                        >
                                            <span
                                                className={cn(
                                                    'size-8 flex-shrink-0 rounded-full',
                                                    state === 'standard'
                                                        ? 'shadow-inner ring-2 ring-white ring-offset-1 ring-offset-gray-200 dark:ring-gray-800 dark:ring-offset-gray-600'
                                                        : DOT_STYLE[state],
                                                )}
                                                style={dotInlineStyle(state, swatch.hex)}
                                                aria-hidden="true"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-[10px] font-medium uppercase tracking-wide text-gray-400">{swatch.label}</p>
                                                {none ? (
                                                    <p className="truncate text-xs text-gray-400">{t(`${P}.no_customization`)}</p>
                                                ) : (
                                                    <p className="truncate text-xs font-semibold text-gray-800 dark:text-gray-100" title={swatch.name ?? ''}>
                                                        {swatch.name}
                                                    </p>
                                                )}
                                                {(state === 'off' || notAccepted) && (
                                                    <span className="mt-0.5 inline-block rounded-full bg-amber-50 px-1.5 text-[9px] font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-800">
                                                        {notAccepted ? t(`${P}.not_accepted_short`) : t(`${P}.off_palette`)}
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    <section className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-3 py-2 text-sm dark:border-gray-700/60">
                        <div className="min-w-0">
                            <p className="text-xs tabular-nums text-gray-500 dark:text-gray-400">{quantity}× {formatBrl(product.unit_price)}</p>
                            <p className="font-bold tabular-nums text-gray-900 dark:text-gray-100">{formatBrl(product.total)}</p>
                        </div>
                        {product.local_product_id && (
                            <Link
                                to={`/products/${product.local_product_id}`}
                                className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                                {t(`${P}.open_product`)}
                                <PiArrowSquareOut className="size-3" />
                            </Link>
                        )}
                    </section>
                </div>
            </div>
        </V2Modal>
    );
}
